import type { Locale } from "./config";

export interface PageCopy {
  headline: string;
  support: string;
  cta: string;
  scope: string;
  emailSubject: string;
  pause: string;
  resume: string;
  pauseHint: string;
  imprint: string;
  privacy: string;
  close: string;
}

export const COPY: Record<Locale, PageCopy> = {
  en: {
    headline: "techmaker.com is for sale.",
    support: "A name built for what’s next in technology.",
    cta: "Get in touch",
    scope: "Offer applies to the techmaker.com domain name only.",
    emailSubject: "Interested in techmaker.com",
    pause: "Pause language rotation",
    resume: "Resume language rotation",
    pauseHint: "Languages change every 20 seconds",
    imprint: "Imprint",
    privacy: "Privacy",
    close: "Close",
  },
  de: {
    headline: "techmaker.com steht zum Verkauf.",
    support: "Ein Name mit Zukunft – für Technologie, die etwas bewegt.",
    cta: "Kontakt aufnehmen",
    scope: "Zum Verkauf steht ausschließlich der Domainname techmaker.com.",
    emailSubject: "Interesse an techmaker.com",
    pause: "Sprachwechsel pausieren",
    resume: "Sprachwechsel fortsetzen",
    pauseHint: "Automatischer Sprachwechsel alle 20 Sekunden",
    imprint: "Impressum",
    privacy: "Datenschutz",
    close: "Schließen",
  },
};

export const LEGAL_COPY: Record<
  Locale,
  {
    imprintTitle: string;
    imprintHtml: string;
    privacyTitle: string;
    privacyHtml: string;
  }
> = {
  en: {
    imprintTitle: "Imprint",
    imprintHtml: [
      "<h3>Information pursuant to Section 5 DDG</h3>",
      "<address><strong>Techmaker GmbH</strong><br>Friedrich-Ebert-Allee 140<br>53113 Bonn<br>Germany</address>",
      "<h3>Commercial register</h3>",
      "<p>Register court: Amtsgericht Bonn<br>Register number: HRB 25761</p>",
      "<h3>Represented by</h3>",
      "<ul><li>Dr. Joachim Schuhmacher, Managing Director</li><li>Lukas Große, Managing Director</li></ul>",
      '<h3>Contact</h3><p><a href="mailto:contact@techmaker.com">contact@techmaker.com</a></p>',
    ].join(""),
    privacyTitle: "Privacy",
    privacyHtml: [
      "<h3>Controller</h3>",
      "<address><strong>Techmaker GmbH</strong><br>Friedrich-Ebert-Allee 140<br>53113 Bonn<br>Germany<br><a href=\"mailto:contact@techmaker.com\">contact@techmaker.com</a></address>",
      "<h3>Hosting and server logs</h3>",
      "<p>This website is hosted by STRATO GmbH, Otto-Ostrowski-Straße 7, 10249 Berlin, Germany. STRATO processes technical access data on our behalf to deliver and secure the website and acts as a processor under Article 28 GDPR.</p>",
      "<p>The logged data can include the requested resource, date and time, transferred data volume, referrer, browser information, response status, and client hostname or IP address. The legal basis is Article 6(1)(f) GDPR. Our legitimate interest is the secure and reliable operation of this website.</p>",
      "<p>STRATO anonymises client hostnames or IP addresses in the log files available to us. Access logs are available to us for six weeks; error logs for 24 hours. We do not combine these logs with other data or use them for analytics.</p>",
      "<h3>Email inquiries</h3>",
      "<p>If you contact us by email, we process the information you provide to answer and manage your domain inquiry. The legal basis is Article 6(1)(b) GDPR for pre-contractual steps and Article 6(1)(f) GDPR for related correspondence.</p>",
      "<p>Unless statutory retention duties or the establishment, exercise, or defence of legal claims require longer storage, inquiry correspondence is deleted no later than 12 months after the last substantive contact.</p>",
      "<h3>Cookies and analytics</h3>",
      "<p>This website uses no analytics, marketing technology, or cookies.</p>",
      "<h3>Your rights</h3>",
      "<p>Subject to the applicable conditions, you may request access, rectification, erasure, restriction, portability, or object to processing. You may also lodge a complaint with a competent supervisory authority.</p>",
    ].join(""),
  },
  de: {
    imprintTitle: "Impressum",
    imprintHtml: [
      "<h3>Angaben gemäß § 5 DDG</h3>",
      "<address><strong>Techmaker GmbH</strong><br>Friedrich-Ebert-Allee 140<br>53113 Bonn<br>Deutschland</address>",
      "<h3>Handelsregister</h3>",
      "<p>Registergericht: Amtsgericht Bonn<br>Registernummer: HRB 25761</p>",
      "<h3>Vertreten durch</h3>",
      "<ul><li>Dr. Joachim Schuhmacher, Geschäftsführer</li><li>Lukas Große, Geschäftsführer</li></ul>",
      '<h3>Kontakt</h3><p><a href="mailto:contact@techmaker.com">contact@techmaker.com</a></p>',
    ].join(""),
    privacyTitle: "Datenschutz",
    privacyHtml: [
      "<h3>Verantwortlicher</h3>",
      "<address><strong>Techmaker GmbH</strong><br>Friedrich-Ebert-Allee 140<br>53113 Bonn<br>Deutschland<br><a href=\"mailto:contact@techmaker.com\">contact@techmaker.com</a></address>",
      "<h3>Hosting und Server-Logfiles</h3>",
      "<p>Diese Website wird bei der STRATO GmbH, Otto-Ostrowski-Straße 7, 10249 Berlin, gehostet. STRATO verarbeitet in unserem Auftrag technische Zugriffsdaten, um die Website bereitzustellen und abzusichern, und wird als Auftragsverarbeiter gemäß Art. 28 DSGVO eingesetzt.</p>",
      "<p>Zu den protokollierten Daten können die aufgerufene Ressource, Datum und Uhrzeit, übertragene Datenmenge, Referrer, Browserinformationen, Antwortstatus sowie Hostname oder IP-Adresse des zugreifenden Geräts gehören. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im sicheren und zuverlässigen Betrieb dieser Website.</p>",
      "<p>STRATO anonymisiert Hostnamen beziehungsweise IP-Adressen in den für uns abrufbaren Logfiles. Zugriffsprotokolle stehen uns sechs Wochen, Fehlerprotokolle 24 Stunden zur Verfügung. Wir führen diese Daten nicht mit anderen Datenbeständen zusammen und verwenden sie nicht zu Analysezwecken.</p>",
      "<h3>E-Mail-Anfragen</h3>",
      "<p>Wenn Sie uns per E-Mail kontaktieren, verarbeiten wir die von Ihnen übermittelten Daten, um Ihre Anfrage zum Domainkauf zu beantworten und abzuwickeln. Rechtsgrundlagen sind Art. 6 Abs. 1 lit. b DSGVO für vorvertragliche Maßnahmen und Art. 6 Abs. 1 lit. f DSGVO für die damit verbundene Korrespondenz.</p>",
      "<p>Sofern keine gesetzlichen Aufbewahrungspflichten oder Gründe für eine längere Speicherung zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen bestehen, löschen wir die Korrespondenz spätestens zwölf Monate nach dem letzten inhaltlichen Kontakt.</p>",
      "<h3>Cookies und Analyse</h3>",
      "<p>Wir setzen weder Cookies noch Analyse- oder Marketingdienste ein.</p>",
      "<h3>Ihre Rechte</h3>",
      "<p>Soweit die gesetzlichen Voraussetzungen vorliegen, haben Sie das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Zudem steht Ihnen ein Beschwerderecht bei einer Datenschutzaufsichtsbehörde zu.</p>",
    ].join(""),
  },
};
